import { useActionState, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { State } from '@/types';
import { changePassword, loginUser, logoutUser } from '@/tmp/UserService';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { createInitState } from '@/utilities/formUtils';
import { useNotificationStore } from '@/stores/notificationstore';
import { ChangePasswordForm } from './ChangePasswordForm';
import { createPassword } from '@/generated/client/passwordreset/passwordreset';
import { getInvitationToken } from './utils/invitationToken';
import { getApiErrorMessageKeyForStatus, getApiErrorStatus } from '@/shared/api/apiError';

export type ChangePasswordFields = {
  newPassword: string;
  newPasswordAgain: string;
};

export default function ChangePasswordPage({
  successfulChange,
  username,
  sudoPassword,
}: {
  successfulChange: (username?: string) => void;
  username: string;
  sudoPassword: string;
}) {
  const changePasswordState = createInitState<ChangePasswordFields>(['newPassword', 'newPasswordAgain']);
  const [state, formAction] = useActionState(tryChangePassword, changePasswordState);
  const [isPhone, setPhone] = useState<boolean>(isPhoneOrHighScreen);
  const invitationToken = getInvitationToken();
  const showNotification = useNotificationStore((state) => state.showNotification);

  const { t } = useTranslation();

  useEffect(() => {
    function handleResize() {
      setPhone(isPhoneOrHighScreen);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  async function tryChangePassword(
    prev: State<ChangePasswordFields>,
    formPayload: ChangePasswordFields
  ): Promise<State<ChangePasswordFields>> {
    const missingNewPassword = !formPayload.newPassword;
    const missingNewPasswordAgain = !formPayload.newPasswordAgain;

    if (missingNewPassword || missingNewPasswordAgain) {
      return {
        ...prev,
        errors: {
          newPassword: missingNewPassword ? t('login.error.missingPassword') : undefined,
          newPasswordAgain: missingNewPasswordAgain ? t('login.error.missingPassword') : undefined,
        },
        globalError: undefined,
        success: false,
      };
    }

    if (
      formPayload.newPassword !== formPayload.newPasswordAgain &&
      (formPayload.newPassword.length && formPayload.newPasswordAgain.length) > 0
    ) {
      return {
        ...prev,
        errors: {
          newPassword: undefined,
          newPasswordAgain: undefined,
        },
        globalError: undefined,
        success: false,
      };
    }

    try {
      if (invitationToken === undefined) {
        await loginUser({ username, password: sudoPassword, rememberMe: false, isBiometricLogin: false });
        await createPassword({ currentPassword: sudoPassword, newPassword: formPayload.newPassword });
        await logoutUser();
      } else {
        await changePassword(username, sudoPassword, formPayload.newPassword, invitationToken);
      }
      showNotification('success', t('login.resetPassword.succesfulChange'));
      successfulChange(username || undefined);
      return {
        globalError: undefined,
        errors: { newPassword: undefined, newPasswordAgain: undefined },
        success: true,
      };
    } catch (error) {
      const status = getApiErrorStatus(error);
      const messageKey = getApiErrorMessageKeyForStatus(status);
      if (messageKey) {
        showNotification('error', t(messageKey));
      } else if (status === 400 || status === 422) {
        showNotification('error', t('login.resetPassword.passwordFormatError'));
      } else {
        showNotification('error', t('login.passwordChangeV3.saveError'));
      }
      return changePasswordState;
    }
  }

  return (
    <ChangePasswordForm
      state={state}
      formAction={formAction}
      isPhone={isPhone}
      username={username}
      sudoPassword={sudoPassword}
    />
  );
}
